import { ArrowRight, Info, Loader2, TrendingDown, TrendingUp } from 'lucide-react';
import { Bar, BarChart, ResponsiveContainer } from 'recharts';

import { cn } from '~shared/shadcn/lib/utils';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  delta?: {
    signedChange: number;
    variant: 'positive' | 'negative' | 'neutral';
    label: string;
    comparisonDetail?: string;
  };
  icon?: React.ReactNode;
  badge?: {
    label: string;
    variant: 'success' | 'warning' | 'danger';
  };
  trendData?: Array<number>;
  trendType?: 'line' | 'bar';
  color?: string;
  onClick?: () => void;
  tooltip?: string;
  chart?: React.ReactNode | null;
  isLoading?: boolean;
  className?: string;
}

export function StatCard({
  title,
  value,
  subtitle,
  delta,
  icon,
  badge,
  trendData,
  trendType,
  color = '#FFC300',
  onClick,
  tooltip,
  chart = null,
  isLoading,
  className,
}: StatCardProps) {
  const chartData = trendData?.map((value) => ({ value })) ?? [];

  return (
    <Card
      className={cn(
        'relative w-full min-w-0 max-w-full overflow-hidden bg-white shadow-sm transition-all duration-200',
        'md:hover:-translate-y-1 md:hover:scale-[1.02] md:hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]',
        'rounded-lg border before:absolute before:inset-x-0 before:top-0 before:h-[2px] before:bg-gradient-to-r before:from-transparent before:via-primary before:to-transparent before:opacity-0 before:transition-opacity before:duration-300 md:hover:before:opacity-100',
        onClick && 'cursor-pointer active:scale-[0.99]',
        // Stack chart under copy on small phones so the donut never forces
        // horizontal overflow / clipping; side-by-side from sm up.
        chart &&
          'flex max-w-full flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-3',
        className,
      )}
      onClick={onClick}
    >
      <CardContent
        className={cn(chart && 'min-w-0 flex-1 overflow-hidden', 'p-4 md:p-6')}
      >
        <CardHeader className='px-0 pb-2 pt-0 md:pb-4'>
          <CardTitle className='flex min-w-0 items-start gap-2 text-[#6B7280]'>
            {icon && <div className='shrink-0'>{icon}</div>}
            <p className='min-w-0 text-xs font-medium leading-snug md:text-sm'>
              {title}
            </p>
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className='size-3.5' />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs text-sm'>
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </CardTitle>
        </CardHeader>

        {isLoading ? (
          <div className='flex items-center justify-center'>
            <Loader2 className='size-4 animate-spin' />
          </div>
        ) : (
          <>
            <div className='space-y-1'>
              <p className='break-words text-2xl font-semibold tabular-nums tracking-tight text-[#000814] md:text-4xl'>
                {value}
              </p>
              {subtitle && (
                <p className='text-xs font-medium text-[#6B7280] md:text-sm'>
                  {subtitle}
                </p>
              )}
            </div>

            {badge && (
              <div className='mt-1'>
                <span
                  className={cn(
                    'inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[10px] font-medium md:px-2.5 md:py-1 md:text-xs',
                    badge.variant === 'success' && 'bg-green-50 text-green-700',
                    badge.variant === 'warning' &&
                      'bg-orange-50 text-orange-700',
                    badge.variant === 'danger' && 'bg-red-50 text-red-700',
                  )}
                >
                  {badge.label}
                </span>
              </div>
            )}

            {(delta ?? trendData) && (
              <div className='flex flex-wrap items-center gap-x-1 gap-y-0.5 border-t border-[#F3F4F6] pt-2 md:pt-3'>
                {delta && (
                  <div className='flex min-w-0 flex-wrap items-center gap-1'>
                    {delta.variant === 'positive' && (
                      <TrendingUp className='size-3 shrink-0 text-[#16A34A] md:size-3.5' />
                    )}
                    {delta.variant === 'negative' && (
                      <TrendingDown className='size-3 shrink-0 text-[#DC2626] md:size-3.5' />
                    )}
                    {delta.variant === 'neutral' && (
                      <ArrowRight className='size-3 shrink-0 text-[#6B7280] md:size-3.5' />
                    )}
                    <span
                      className={cn(
                        'truncate text-xs font-medium tabular-nums md:text-sm',
                        delta.variant === 'positive' && 'text-[#16A34A]',
                        delta.variant === 'negative' && 'text-[#DC2626]',
                        delta.variant === 'neutral' && 'text-[#6B7280]',
                      )}
                    >
                      {delta.label}
                    </span>
                    <span className='hidden truncate text-xs text-[#9CA3AF] md:inline'>
                      vs last month
                      {delta.comparisonDetail
                        ? ` ${delta.comparisonDetail}`
                        : ''}
                    </span>
                  </div>
                )}
                {trendData && trendData.length > 0 && trendType === 'bar' && (
                  <div className='hidden h-8 w-24 md:block'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <BarChart data={chartData}>
                        <Bar
                          dataKey='value'
                          fill={color}
                          radius={[4, 4, 0, 0]}
                          isAnimationActive={true}
                          animationDuration={150}
                          animationBegin={0}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
      {chart && (
        <div className='flex shrink-0 items-center justify-center self-center overflow-hidden px-4 pb-4 sm:w-24 sm:max-w-[40%] sm:self-center sm:p-1 md:w-1/2 md:max-w-[50%] md:p-0'>
          <div className='mx-auto size-[88px] max-w-full sm:size-auto sm:aspect-square sm:w-full sm:max-w-[96px] md:max-w-[200px]'>
            {chart}
          </div>
        </div>
      )}
    </Card>
  );
}
